import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Building2, Plus, Check, Users, Sun, Moon, Monitor, ChevronRight, Search } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { InvitationNotification } from '../Sidebar/InvitationNotification';
import { SearchOverlay } from '../Sidebar/SearchOverlay';

interface MobileTopBarProps {
  isGlobalDarkMode: boolean;
  setIsGlobalDarkMode: (dark: boolean) => void;
}

export const MobileTopBar: React.FC<MobileTopBarProps> = ({ isGlobalDarkMode, setIsGlobalDarkMode }) => {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { currentWorkspace, workspaces, setCurrentWorkspace } = useWorkspace();
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [themePreference, setThemePreference] = useState<'light' | 'dark' | 'system'>('dark');
  const workspaceMenuRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const themeSelectorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (workspaceMenuRef.current && !workspaceMenuRef.current.contains(event.target as Node)) {
        setShowWorkspaceMenu(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
        setShowThemeSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleWorkspaceChange = async (workspace: any) => {
    await setCurrentWorkspace(workspace);
    setShowWorkspaceMenu(false);
  };

  return (
    <>
      <div
        className={`mobile-top-bar fixed top-0 left-0 right-0 border-b z-40 ${
          isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
        }`}
        style={{ height: '3.5rem' }}
      >
        <div className="flex items-center justify-between h-full px-4">
          <button
            onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
            className="flex items-center justify-center w-10 h-10 rounded-full active:bg-gray-100 dark:active:bg-sidebar-accent"
          >
            <img
              src={currentWorkspace?.favicon || (isGlobalDarkMode ? '/icon-white.svg' : '/icon-blue.svg')}
              alt={currentWorkspace?.name || 'AgencyPlus'}
              className="w-10 h-10 object-contain"
            />
          </button>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowSearchOverlay(true)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                isGlobalDarkMode ? 'hover:bg-sidebar-accent' : 'hover:bg-gray-100'
              }`}
            >
              <Search className={`w-5 h-5 ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <InvitationNotification isGlobalDarkMode={isGlobalDarkMode} align="right" verticalAlign="bottom" />
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-95 ${
                isGlobalDarkMode ? 'hover:bg-sidebar-accent' : 'hover:bg-gray-100'
              }`}
            >
              <div className={`w-7 h-7 rounded-full flex items-center justify-center overflow-hidden ${
                isGlobalDarkMode ? 'bg-sidebar-accent' : 'bg-gray-300'
              }`}>
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className={`w-4 h-4 ${isGlobalDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                )}
              </div>
            </button>
          </div>
        </div>
      </div>

      {showWorkspaceMenu && (
        <div
          ref={workspaceMenuRef}
          className={`fixed top-16 left-4 right-4 rounded-lg shadow-xl border z-[100] max-h-80 overflow-y-auto ${
            isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`px-3 py-2 border-b flex items-center justify-between ${
            isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200'
          }`}>
            <p className={`text-xs font-semibold ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
              Switch Workspace
            </p>
            <button
              onClick={() => {
                navigate('?modal=settings&tab=workspace');
                setShowWorkspaceMenu(false);
              }}
              className={`p-1 rounded-md transition-colors ${
                isGlobalDarkMode
                  ? 'hover:bg-sidebar text-sidebar-foreground/60 hover:text-sidebar-foreground'
                  : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
              }`}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="p-1.5">
            {workspaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => handleWorkspaceChange(workspace)}
                className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md transition-colors ${
                  currentWorkspace?.id === workspace.id
                    ? isGlobalDarkMode
                      ? 'bg-nav-active text-white'
                      : 'bg-workspace-accent/10 text-workspace-accent'
                    : isGlobalDarkMode
                    ? 'hover:bg-sidebar text-sidebar-foreground/80'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  {workspace.favicon ? (
                    <img src={workspace.favicon} alt={workspace.name} className="w-7 h-7 object-contain" />
                  ) : (
                    <div className={`w-7 h-7 rounded-md flex items-center justify-center ${
                      workspace.color ? '' : isGlobalDarkMode ? 'bg-sidebar' : 'bg-gray-200'
                    }`} style={{ backgroundColor: workspace.color || undefined }}>
                      <Building2 className="w-3.5 h-3.5 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-medium truncate">{workspace.name}</p>
                </div>
                {currentWorkspace?.id === workspace.id && <Check className="w-3.5 h-3.5 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}

      {showProfileMenu && (
        <div
          ref={profileMenuRef}
          className={`fixed top-16 right-4 w-80 rounded-xl border shadow-2xl z-[100] ${
            isGlobalDarkMode ? 'bg-sidebar-accent border-sidebar-border' : 'bg-white border-gray-200'
          }`}
        >
          <div className={`p-3 border-b ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'}`}>
            <p className={`text-xs font-semibold mb-2 px-2 ${
              isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'
            }`}>
              Accounts
            </p>
            <button
              onClick={() => {
                navigate('?modal=settings&tab=profile');
                setShowProfileMenu(false);
              }}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                isGlobalDarkMode ? 'hover:bg-sidebar' : 'hover:bg-gray-100'
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                isGlobalDarkMode ? 'bg-sidebar' : 'bg-gray-200'
              }`}>
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className={`w-5 h-5 ${isGlobalDarkMode ? 'text-sidebar-foreground/70' : 'text-gray-600'}`} />
                )}
              </div>
              <div className="flex-1 text-left">
                <p className={`text-sm font-semibold ${isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900'}`}>
                  {profile?.first_name || profile?.last_name
                    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                    : 'User Profile'}
                </p>
                {profile?.email && (
                  <p className={`text-xs ${isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'}`}>
                    {profile.email}
                  </p>
                )}
              </div>
              <ChevronRight className={`w-4 h-4 ${isGlobalDarkMode ? 'text-sidebar-foreground/40' : 'text-gray-400'}`} />
            </button>
          </div>

          <div className={`p-3 border-b ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'}`}>
            <p className={`text-xs font-semibold mb-2 px-2 ${
              isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-500'
            }`}>
              Workspace
            </p>
            <button
              onClick={() => {
                navigate('?modal=settings&tab=workspace&workspaceTab=members');
                setShowProfileMenu(false);
              }}
              className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg border transition-all ${
                isGlobalDarkMode
                  ? 'border-sidebar-border hover:bg-sidebar text-sidebar-foreground/80'
                  : 'border-gray-200 hover:bg-gray-100 text-gray-700'
              }`}
            >
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Manage Members</span>
            </button>
          </div>

          <div className="p-2">
            <button
              onClick={() => {
                navigate('?modal=settings&tab=profile');
                setShowProfileMenu(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isGlobalDarkMode ? 'text-sidebar-foreground/80 hover:bg-sidebar' : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings className="w-4 h-4" />
              <span className="font-medium">Settings</span>
            </button>

            {/* Theme toggle */}
            <div className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm ${
              isGlobalDarkMode ? 'text-gray-300' : 'text-gray-700'
            }`}>
              <div className="flex items-center gap-3">
                <Monitor className="w-4 h-4" />
                <span className="font-medium">Theme</span>
              </div>
              <button
                aria-label="Toggle theme"
                onClick={() => {
                  const next = !isGlobalDarkMode;
                  setIsGlobalDarkMode(next);
                  setThemePreference(next ? 'dark' : 'light');
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  isGlobalDarkMode ? 'bg-indigo-600' : 'bg-gray-300'
                }`}
              >
                <span className={`pointer-events-none inline-flex h-5 w-5 transform items-center justify-center rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isGlobalDarkMode ? 'translate-x-5' : 'translate-x-0'
                }`}>
                  {isGlobalDarkMode
                    ? <Moon className="w-3 h-3 text-indigo-600" />
                    : <Sun className="w-3 h-3 text-amber-500" />}
                </span>
              </button>
            </div>
          </div>

          <div className={`p-2 border-t ${isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-100'}`}>
            <button
              onClick={async () => {
                await signOut();
                navigate('/login');
                setShowProfileMenu(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isGlobalDarkMode ? 'text-sidebar-foreground/80 hover:text-red-400 hover:bg-red-500/10' : 'text-gray-700 hover:text-red-600 hover:bg-red-50'
              }`}
            >
              <LogOut className="w-4 h-4" />
              <span className="font-medium">Log out</span>
            </button>
          </div>
        </div>
      )}

      <SearchOverlay
        open={showSearchOverlay}
        onOpenChange={setShowSearchOverlay}
        isGlobalDarkMode={isGlobalDarkMode}
      />
    </>
  );
};
